@if (store.loading()) {
  <p class="pdw-waiting" role="status" aria-live="polite">Loading case…</p>
} @else if (store.signInRedirect()) {
  <div class="pdw-panel" role="status" aria-live="polite">
    <p>Redirecting to sign in. If this page does not redirect, retry.</p>
    <button (click)="store.load(caseId())" type="button" class="gate__btn gate__btn--primary">
      <i class="pi pi-refresh" aria-hidden="true"></i> Retry
    </button>
  </div>
} @else if (store.loadFailed()) {
  <div class="pdw-panel" role="alert">
    <p>{{ store.caseMissing() ? 'This workflow case no longer exists.' : store.loadStatus() === 403 ? 'You do not have access to this workflow.' : 'The workflow could not be loaded. Try again.' }}</p>
    <button (click)="store.load(caseId())" type="button" class="gate__btn gate__btn--primary">
      <i class="pi pi-refresh" aria-hidden="true"></i> Retry
    </button>
  </div>
} @else if (state(); as s) {
  <section class="pdw" aria-label="Case workflow">
    <header class="pdw-head">
      <div class="pdw-head__main">
        <div class="pdw-head__title-row">
          <span class="pdw-head__kind">{{ labelFor(s.caseType) }}</span>
          <span class="pdw-head__state" [class.pdw-head__state--closed]="isClosed()">
            {{ isClosed() ? 'Closed' : (store.graph().find(node => node.status === 'active')?.title ?? 'Open') }}
          </span>
          @if (s.outcome; as outcome) {
            <span class="pdw-head__outcome">{{ labelFor(outcome) }}</span>
          }
        </div>
        <h3 class="pdw-head__title">{{ s.caseNumber }}</h3>
        @if (!isClosed()) { <p class="pdw-head__owner">{{ s.ownerOid ? ownerName() : roleLabel(store.stepRole()) + ' queue' }}</p> }
        <p class="pdw-head__sub">Workflow version {{ s.classVersion }}</p>
      </div>
      <dl class="pdw-head__facts">
        <div>
          <dt>Opened</dt>
          <dd>{{ s.openedOnUtc | date: 'MMM d, HH:mm' : '+0100' }} WAT</dd>
        </div>
        @if (s.closedOnUtc) {
          <div>
            <dt>Closed</dt>
            <dd>{{ s.closedOnUtc | date: 'MMM d, HH:mm' : '+0100' }} WAT</dd>
          </div>
        }
        <div>
          <dt>Signals</dt>
          <dd>{{ store.composed()?.dossier?.signals?.length ?? '--' }}</dd>
        </div>
      </dl>
    </header>

    <nav class="eng-tabs" role="tablist" aria-label="Case detail">
      <button type="button" role="tab" id="eng-summary-tab" aria-controls="eng-summary-panel"
        [attr.aria-selected]="activeTab() === 'summary'" [class.eng-tabs__active]="activeTab() === 'summary'"
        (click)="activeTab.set('summary')">summary</button>
      <button type="button" role="tab" id="eng-workflow-tab" aria-controls="eng-workflow-panel"
        [attr.aria-selected]="activeTab() === 'workflow'" [class.eng-tabs__active]="activeTab() === 'workflow'"
        (click)="activeTab.set('workflow')">workflow</button>
      <button type="button" role="tab" id="eng-recommended-tab" aria-controls="eng-recommended-panel"
        [attr.aria-selected]="activeTab() === 'recommended'" [class.eng-tabs__active]="activeTab() === 'recommended'"
        (click)="activeTab.set('recommended')">recommended actions</button>
      <button type="button" role="tab" id="eng-hypotheses-tab" aria-controls="eng-hypotheses-panel"
        [attr.aria-selected]="activeTab() === 'hypotheses'" [class.eng-tabs__active]="activeTab() === 'hypotheses'"
        (click)="activeTab.set('hypotheses')">hypotheses</button>
      <button type="button" role="tab" id="eng-comments-tab" aria-controls="eng-comments-panel"
        [attr.aria-selected]="activeTab() === 'comments'" [class.eng-tabs__active]="activeTab() === 'comments'"
        (click)="activeTab.set('comments')">comments</button>
      <button type="button" role="tab" id="eng-investigation-tab" aria-controls="eng-investigation-panel"
        [attr.aria-selected]="activeTab() === 'investigation'" [class.eng-tabs__active]="activeTab() === 'investigation'"
        (click)="activeTab.set('investigation')">investigation</button>
    </nav>

    @if (activeTab() === 'summary') {
      <div id="eng-summary-panel" role="tabpanel" aria-labelledby="eng-summary-tab" class="pdw-panel">
        <h4 class="pdw-panel__title">summary</h4>
        @if (candidates()[0]; as candidate) {
          <p>{{ candidateTitle(candidate.explanation, candidate.candidateId) }}</p>
        } @else {
          <p>No candidate explanation is available for this case.</p>
        }
      </div>
    }

    @if (activeTab() === 'workflow') {
    <div id="eng-workflow-panel" role="tabpanel" aria-labelledby="eng-workflow-tab" class="eng-workflow-panel">
    <app-uc2-lifecycle-rail [stages]="railStages()" ariaLabel="Case workflow progress" />

    @if (store.form(); as form) {
      <app-engine-step-panel
        [form]="form"
        [fields]="store.fields()"
        [candidates]="store.candidateOptions()"
        [canClaim]="store.canClaim()"
        [enforceUc2Decisions]="isUc2()"
        [claimOnSubmit]="isUc2()"
        [allowRelease]="!isUc2()"
        [canDecide]="store.canDecide()"
        [isOwner]="store.isOwner()"
        [ownerName]="ownerName()"
        [returnToName]="returnToName()"
        [busy]="store.busy()"
        [commandError]="store.commandError()"
        (claim)="store.claim()"
        (decide)="onDecide($event)"
        (release)="store.release($event)" />
    } @else if (store.commandError(); as error) {
      <p class="gate__blocked" role="alert">{{ error }}</p>
    }

    @if (candidates().length > 0) {
      <div class="pdw-panel">
        <h4 class="pdw-panel__title">Candidate explanations</h4>
        <ol class="eng-list">
          @for (candidate of candidates(); track candidate.candidateId) {
            <li>
              <span class="eng-list__main">{{ candidateTitle(candidate.explanation, candidate.candidateId) }}</span>
              <span class="gate__muted">Rank {{ candidate.rank }}</span>
            </li>
          }
        </ol>
      </div>
    }

    @if (suggestedSignals().length > 0) {
      <div class="pdw-panel">
        <h4 class="pdw-panel__title">Signals awaiting your decision</h4>
        <p class="gate__muted">These arrived after the case was claimed. Accept to add them to the case.</p>
        <ul class="eng-list">
          @for (signal of suggestedSignals(); track signal.signalKey) {
            <li>
              <span class="eng-list__main">{{ signal.signalKey }}</span>
              @if (store.isOwner()) {
                <span class="eng-list__actions">
                  <button
                    [disabled]="store.busy()"
                    (click)="store.resolveSuggestion(signal.signalKey, false)"
                    type="button"
                    class="gate__btn gate__btn--ghost">
                    Reject
                  </button>
                  <button
                    [disabled]="store.busy()"
                    (click)="store.resolveSuggestion(signal.signalKey, true)"
                    type="button"
                    class="gate__btn gate__btn--primary">
                    Accept
                  </button>
                </span>
              }
            </li>
          }
        </ul>
      </div>
    }

    <div class="pdw-panel">
      <h4 class="pdw-panel__title">Audit trail</h4>
      <app-workflow-audit-trail [trail]="auditTrail()" />
    </div>
    </div>
    }

    @if (activeTab() === 'recommended') {
      <div id="eng-recommended-panel" role="tabpanel" aria-labelledby="eng-recommended-tab" class="pdw-panel">
        <h4 class="pdw-panel__title">recommended actions</h4>
        <p>AgentCase recommendations are not available for this engine-only case.</p>
      </div>
    }

    @if (activeTab() === 'hypotheses') {
      <div id="eng-hypotheses-panel" role="tabpanel" aria-labelledby="eng-hypotheses-tab" class="pdw-panel">
        <h4 class="pdw-panel__title">hypotheses</h4>
        @if (candidates().length) {
          <ol class="eng-list">
            @for (candidate of candidates(); track candidate.candidateId) {
              <li><span class="eng-list__main">{{ candidateTitle(candidate.explanation, candidate.candidateId) }}</span>
                <span>Rank {{ candidate.rank }}</span></li>
            }
          </ol>
        } @else {
          <p>No candidate explanations are available.</p>
        }
      </div>
    }

    @if (activeTab() === 'comments') {
      <div id="eng-comments-panel" role="tabpanel" aria-labelledby="eng-comments-tab" class="pdw-panel">
        <h4 class="pdw-panel__title">comments</h4>
        <p>Comments are not available for engine-only cases.</p>
      </div>
    }

    @if (activeTab() === 'investigation') {
      <div id="eng-investigation-panel" role="tabpanel" aria-labelledby="eng-investigation-tab" class="pdw-panel">
        <h4 class="pdw-panel__title">investigation</h4>
        @if (store.composed()?.dossier?.signals; as signals) {
          <ul class="eng-list">
            @for (signal of signals; track signal.signalKey) {
              <li><span class="eng-list__main">{{ signal.signalKey }}</span><span>{{ signal.attachMode }}</span></li>
            } @empty {
              <li>No signals are available for this case.</li>
            }
          </ul>
        } @else {
          <p>No signal evidence is available for this case.</p>
        }
      </div>
    }
  </section>
}
